import { newUuid } from "@overlayqa/shared";
import { useState } from "react";
import { useAuth } from "../../lib/auth";
import { reviewShareUrl } from "../../lib/reviewShareUrl";
import {
  ClientReviewVersionHistory,
  ClientReviewNewVersion,
  ClientReviewVersionCompare,
  VersionDialog,
  useVersionCaptureRequest,
  type ReviewVersionItem,
} from "@overlayqa/shared/ui/client-review-versions";
import {
  useClientReviewVersions,
  useCreateClientReviewVersion,
} from "../../hooks/useClientReviews";
import { SelectDropdown } from "../shared/SelectDropdown";
import type { ClientReview } from "@overlayqa/shared";

export function ClientReviewVersions({ review }: { review: ClientReview }) {
  const { getToken } = useAuth();
  const [dialog, setDialog] = useState<"history" | "new" | "compare" | null>(
    null,
  );
  const [requestId, setRequestId] = useState<string>(() => newUuid());
  const captureRequest = useVersionCaptureRequest();
  const query = useClientReviewVersions(review.id, !!dialog);
  const create = useCreateClientReviewVersion();
  const data = query.data;
  async function loadComments(version: ReviewVersionItem): Promise<string[]> {
    const url = new URL(reviewShareUrl(review.token));
    url.pathname += "/comments";
    url.searchParams.set("version", version.id);
    const token = await getToken();
    const response = await fetch(url, {
      credentials: "include",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!response.ok) throw new Error("Could not load feedback");
    const json = await response.json();
    if (json.error) throw new Error("Could not load feedback");
    return (json.comments ?? json.data?.comments ?? []).map(
      (comment: { commentText: string }) => comment.commentText,
    );
  }
  function open(v: ReviewVersionItem) {
    window.open(v.shareUrl, "_blank", "noopener,noreferrer");
  }
  function capture(name: string, captureRequestId = requestId) {
    if (!data) return;
    create.mutate(
      {
        reviewId: review.id,
        ...captureRequest({ name, requestId: captureRequestId, baseVersionId: data.currentVersionId }),
      },
      {
        onSuccess: () => {
          setRequestId(newUuid());
          setDialog("history");
          void query.refetch();
        },
      },
    );
  }
  return (
    <>
      <button
        type="button"
        className="btn-ghost btn-sm whitespace-nowrap"
        onClick={() => setDialog("history")}
      >
        <span className="text-cap-trim">Version history</span>
      </button>
      {dialog === "history" &&
        (data ? (
          <ClientReviewVersionHistory
            data={{ ...data, selectedVersionId: null }}
            error={create.error?.message}
            onClose={() => setDialog(null)}
            onOpen={open}
            earlierUrl={`${reviewShareUrl(review.token)}?version=earlier`}
            onRetry={(version) =>
              capture(version.name ?? "", version.requestId ?? requestId)
            }
            onEarlier={() => {
              const url = new URL(data.versions[0]?.shareUrl ?? location.href);
              url.searchParams.set("version", "earlier");
              window.open(url.href, "_blank", "noopener,noreferrer");
            }}
            onNew={() => {
              setRequestId(newUuid());
              create.reset();
              setDialog("new");
            }}
            onCompare={() => setDialog("compare")}
          />
        ) : (
          <VersionDialog title="Version history" close={() => setDialog(null)}>
            <p
              className="oq-text-body"
              role={query.isError ? "alert" : "status"}
            >
              {query.isError
                ? "Could not load saved versions. Please try again."
                : "Loading saved versions..."}
            </p>
            {query.isError && (
              <button
                type="button"
                className="oq-btn oq-btn--secondary"
                onClick={() => void query.refetch()}
              >
                <span className="text-cap-trim">Try again</span>
              </button>
            )}
          </VersionDialog>
        ))}
      {dialog === "new" && data && (
        <ClientReviewNewVersion
          pageUrl={review.pageUrl}
          viewport={
            review.creatorViewportWidth
              ? `${review.creatorViewportWidth} × ${review.creatorViewportHeight}`
              : "Current page viewport"
          }
          busy={create.isPending}
          error={create.error ?? ""}
          onClose={() => setDialog(null)}
          onCapture={capture}
        />
      )}{" "}
      {dialog === "compare" && data && (
        <VersionDialog
          title="Compare versions"
          close={() => setDialog(null)}
          wide
        >
          <ClientReviewVersionCompare
            embedded
            versions={data.versions}
            loadComments={loadComments}
            selectedVersionId={data.currentVersionId}
            onClose={() => setDialog(null)}
            onOpen={(version) => {
              const url = new URL(version.shareUrl);
              url.searchParams.set("preview", "client");
              window.open(url.href, "_blank", "noopener,noreferrer");
            }}
            renderPicker={(label, value, options, onChange) => (
              <div className="vv-pair-choice">
                <span className="oq-text-label">{label}</span>
                <SelectDropdown
                  aria-label={label}
                  value={value}
                  options={options}
                  onChange={onChange}
                />
              </div>
            )}
          />
        </VersionDialog>
      )}
    </>
  );
}
